/**
 * Centralized Debug Logger
 * 
 * Provides structured logging with context and color coding
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'success';

interface LogContext {
  component: string;
  method?: string;
  gameId?: string;
  playerId?: number;
}

class DebugLogger {
  private enabled: boolean = true;
  private logHistory: Array<{ timestamp: Date; level: LogLevel; message: string; context?: LogContext }> = [];

  constructor() {
    // Always enable in development
    this.enabled = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext, data?: any): string {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    const contextStr = context ? `[${context.component}${context.method ? '.' + context.method : ''}${context.gameId ? ` gameId:${context.gameId.slice(-6)}` : ''}${context.playerId ? ` player:${context.playerId}` : ''}]` : '';
    
    return `${timestamp} ${contextStr} ${message}`;
  }

  private log(level: LogLevel, message: string, context?: LogContext, data?: any) {
    if (!this.enabled) return;

    const formattedMessage = this.formatMessage(level, message, context, data);
    
    // Store in history
    this.logHistory.push({
      timestamp: new Date(),
      level,
      message,
      context
    });

    // Keep only last 100 logs
    if (this.logHistory.length > 100) {
      this.logHistory.shift();
    }

    // Console output with colors
    const styles: Record<LogLevel, string> = {
      debug: 'color: gray',
      info: 'color: blue',
      warn: 'color: orange',
      error: 'color: red; font-weight: bold',
      success: 'color: green; font-weight: bold'
    };

    console.log(`%c${formattedMessage}`, styles[level]);
    
    if (data !== undefined) {
      console.log('  Data:', data);
    }
  }

  debug(message: string, context?: LogContext, data?: any) {
    this.log('debug', message, context, data);
  }

  info(message: string, context?: LogContext, data?: any) {
    this.log('info', message, context, data);
  }

  warn(message: string, context?: LogContext, data?: any) {
    this.log('warn', message, context, data);
  }

  error(message: string, context?: LogContext, data?: any) {
    this.log('error', message, context, data);
  }

  success(message: string, context?: LogContext, data?: any) {
    this.log('success', message, context, data);
  }

  getHistory() {
    return [...this.logHistory];
  }

  clearHistory() {
    this.logHistory = [];
  }
}

// Singleton instance
export const logger = new DebugLogger();

